import { getVisualAssetEntry, VisualAssetDimensions } from '../data/assets/visualAssets';

type CharacterRole = 'player' | 'enemy';

interface DisplaySizedSprite {
  readonly width?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  setDisplaySize?: (width: number, height: number) => unknown;
  setData?: (key: string, value: unknown) => unknown;
  body?: {
    setSize?: (width: number, height: number, center?: boolean) => unknown;
  };
}

const DEFAULT_DISPLAY_SIZE: Record<CharacterRole, VisualAssetDimensions> = {
  player: { width: 38, height: 38 },
  enemy: { width: 36, height: 40 }
};

const COLLISION_BODY_RATIO = 0.8;

export function resolveCharacterDisplaySize(
  textureKey: string,
  role: CharacterRole,
  contentScale: number = 1
): VisualAssetDimensions {
  const baseSize = getVisualAssetEntry(textureKey)?.displaySize ?? DEFAULT_DISPLAY_SIZE[role];
  const scale = Number.isFinite(contentScale) && contentScale > 0 ? contentScale : 1;

  return {
    width: Math.round(baseSize.width * scale) * 2,
    height: Math.round(baseSize.height * scale) * 2
  };
}

export function applyCharacterDisplaySize(
  sprite: DisplaySizedSprite | null | undefined,
  textureKey: string,
  role: CharacterRole,
  contentScale: number = 1
): VisualAssetDimensions {
  const displaySize = resolveCharacterDisplaySize(textureKey, role, contentScale);
  if (!sprite) {
    return displaySize;
  }

  sprite.setDisplaySize?.(displaySize.width, displaySize.height);
  sprite.setData?.('visualDisplayWidth', displaySize.width);
  sprite.setData?.('visualDisplayHeight', displaySize.height);

  const bodyWidth = resolveBodySourceSize(displaySize.width, sprite.scaleX);
  const bodyHeight = resolveBodySourceSize(displaySize.height, sprite.scaleY);
  sprite.body?.setSize?.(
    Math.max(1, Math.round(bodyWidth * COLLISION_BODY_RATIO)),
    Math.max(1, Math.round(bodyHeight * COLLISION_BODY_RATIO)),
    true
  );

  return displaySize;
}

function resolveBodySourceSize(displaySize: number, scale: number | undefined): number {
  const absScale = Math.abs(Number(scale));
  if (!Number.isFinite(absScale) || absScale <= 0) {
    return displaySize;
  }

  return displaySize / absScale;
}
